import { useSyncExternalStore } from 'react';
import type { ThemeMode } from './roleTokenValues';

/**
 * The resolved theme, read from the `dark` class on `<html>`.
 *
 * The class is the single source of truth for the resolved mode on web: the
 * pre-paint bootstrap stamps it and `ThemeManager` keeps it current. Watching
 * it directly keeps shared-ui free of a dependency on shared-logic's
 * preference store while still re-rendering subscribers the moment the mode
 * flips.
 *
 * Imports only from modules without `.web.ts` siblings (see the Metro
 * self-import trap documented in `roleTokens.web.ts`).
 */

const subscribe = (onChange: () => void): (() => void) => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => undefined;
  }
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  return () => observer.disconnect();
};

const getSnapshot = (): ThemeMode =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light';

export const useThemeMode = (): ThemeMode =>
  useSyncExternalStore(subscribe, getSnapshot, () => 'light');
